function featureEnabled(snapshot, key) {
  const items = (((snapshot || {}).entitlements || {}).features || []);
  const match = items.find((item) => item.key === key);
  return Boolean(match && match.enabled);
}

function formatToggle(enabled) {
  return enabled ? "enabled" : "disabled";
}

function roleLabel(role) {
  return String(role || "unknown")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function actionableStatus(status) {
  return status === "action_required" || status === "attention_required" || status === "needs_configuration";
}

function statusTone(status) {
  if (status === "completed" || status === "ready") {
    return "ready";
  }
  if (status === "blocked_by_plan" || status === "stale" || status === "attention_required") {
    return "stale";
  }
  if (status === "action_required" || status === "needs_configuration") {
    return "overdue";
  }
  return "muted";
}

function queuePresetSummary(settings) {
  if (!settings) {
    return "Queue defaults unavailable.";
  }
  return [
    `${settings.default_pull_limit}/${settings.max_pull_limit} pull capacity`,
    settings.operator_pull_enabled ? "operator pull enabled" : "operator pull disabled",
    settings.manager_dispatch_enabled ? "manager dispatch enabled" : "manager dispatch disabled",
  ].join(" | ");
}

function automationPresetSummary(settings) {
  if (!settings) {
    return "Automation follows current plan limits.";
  }
  return [
    settings.auto_assign_enabled ? "auto assign enabled" : "auto assign disabled",
    `batch ${settings.auto_assign_batch_size}`,
    settings.auto_escalate_overdue_enabled ? "overdue escalation enabled" : "overdue escalation disabled",
  ].join(" | ");
}

function alertPresetSummary(settings) {
  if (!settings) {
    return "Alerts are unavailable on the current plan.";
  }
  return [
    settings.enabled ? "alerts enabled" : "alerts disabled",
    `cooldown ${settings.cooldown_minutes}m`,
    settings.overdue_total_threshold ? `overdue ${settings.overdue_total_threshold}` : "thresholds optional",
  ].join(" | ");
}

function digestPresetSummary(settings) {
  if (!settings) {
    return "Digests are unavailable on the current plan.";
  }
  return [
    settings.enabled ? "digests enabled" : "digests disabled",
    settings.daily_enabled ? "daily" : null,
    settings.weekly_enabled ? "weekly" : null,
    `${settings.delivery_hour_utc}:00 UTC`,
  ]
    .filter(Boolean)
    .join(" | ");
}

function resolveStepAction(step) {
  const explicitPath = step.action_path || "";
  const key = String(step.key || "");
  const actionLabel = String(step.action_label || "").toLowerCase();
  if (step.status === "blocked_by_plan" || actionLabel.includes("tenant plan") || actionLabel.includes("review plan")) {
    return {
      targetId: "admin-settings-tenant-section",
      label: step.action_label || "Review tenant plan",
    };
  }
  if (explicitPath === "/api/workspace/members" || key === "owner_coverage" || key === "frontline_coverage" || key === "workspace") {
    return {
      targetId: "admin-onboarding-roster",
      label: step.action_label || "Review roster coverage",
    };
  }
  if (key === "plan_selection") {
    return {
      targetId: "admin-settings-tenant-section",
      label: step.action_label || "Review tenant plan",
    };
  }
  if (key === "queue_policy_review" || key === "policy") {
    return {
      targetId: "admin-settings-tenant-section",
      label: step.action_label || "Review queue settings",
    };
  }
  if (key === "automation_review" || key === "automation") {
    return {
      targetId: "admin-settings-automation-section",
      label: step.action_label || "Review automation settings",
    };
  }
  if (key === "alerts_review" || key === "alerts" || key === "notifications") {
    return {
      targetId: "admin-settings-alerts-section",
      label: step.action_label || "Review alert settings",
    };
  }
  if (key === "digests_review" || key === "digests") {
    return {
      targetId: "admin-settings-digests-section",
      label: step.action_label || "Review digest settings",
    };
  }
  if (key === "reporting") {
    return {
      targetId: "admin-reporting-controls",
      label: step.action_label || "Review reporting defaults",
    };
  }
  if (explicitPath === "/api/admin/control-center") {
    return {
      targetId: "admin-form",
      label: step.action_label || "Review owner settings",
    };
  }
  return {
    targetId: "admin-onboarding-helpers",
    label: step.action_label || "Review onboarding helpers",
  };
}

function rosterRows(controlCenter) {
  const members = controlCenter.workspace_members || [];
  if (!members.length) {
    return [
      {
        label: "No active workspace members",
        meta: "Invite an owner and at least one frontline user before go-live.",
        status: "overdue",
      },
    ];
  }
  return members
    .slice()
    .sort((left, right) => String(left.role || "").localeCompare(String(right.role || "")) || String(left.full_name || "").localeCompare(String(right.full_name || "")))
    .map((member) => ({
      label: member.full_name || member.email || "Unknown member",
      meta: `${roleLabel(member.role)} | ${member.email || "email unavailable"}`,
      status: member.status === "active" ? "ready" : "stale",
    }));
}

export function buildOnboardingJourney(controlCenter, options = {}) {
  const onboarding = controlCenter.onboarding || {};
  const role = options.role || "operator";
  const locked = role !== "tenant_owner";
  const steps = (onboarding.steps || []).map((step) => ({
    ...step,
    tone: statusTone(step.status),
    actionable: actionableStatus(step.status),
    action: resolveStepAction(step),
  }));
  const currentStep = steps.find((step) => step.actionable) || steps[0] || null;
  const spotlight =
    onboarding.status === "ready"
      ? {
          title: "Ready for go-live",
          summary: "The onboarding checklist is complete. Use reporting and smoke helpers to validate the tenant before launch.",
          tone: "ready",
          actionLabel: "Review reporting",
          targetId: "admin-reporting-controls",
        }
      : currentStep
        ? {
            title: `Next step: ${currentStep.title}`,
            summary: currentStep.summary || onboarding.next_step_summary || "Continue the onboarding checklist.",
            tone: currentStep.tone,
            actionLabel: currentStep.action.label,
            targetId: currentStep.action.targetId,
          }
        : {
            title: "Onboarding path unavailable",
            summary: "No onboarding steps were returned for this tenant.",
            tone: "stale",
            actionLabel: "Review control center",
            targetId: "admin-form",
          };

  return {
    locked,
    spotlight,
    rosterRows: rosterRows(controlCenter),
    stepCards: steps,
    recommendationCards: (onboarding.recommended_defaults || []).map((item) => ({
      ...item,
      isCurrent: item.key === onboarding.operating_style,
      queueSummary: queuePresetSummary(item.queue_settings),
      automationSummary: automationPresetSummary(item.automation_settings),
      alertSummary: alertPresetSummary(item.alert_settings),
      digestSummary: digestPresetSummary(item.digest_settings),
    })),
    helperCards: onboarding.helpers || { health_url: "", product_ui_url: "", commands: [] },
    premiumFeatures: {
      automation: featureEnabled(controlCenter, "queue_automation"),
      alerts: featureEnabled(controlCenter, "queue_alerts"),
      digests: featureEnabled(controlCenter, "queue_digests"),
    },
  };
}

function setFieldValue(form, name, value) {
  const field = form.elements.namedItem(name);
  if (!field) {
    return;
  }
  if (field.type === "checkbox") {
    field.checked = Boolean(value);
    return;
  }
  field.value = value === null || value === undefined ? "" : String(value);
}

export function applyOnboardingRecommendation(form, recommendation, controlCenter) {
  if (!form || !recommendation) {
    return false;
  }
  const queueSettings = recommendation.queue_settings || {};
  setFieldValue(form, "default_pull_limit", queueSettings.default_pull_limit);
  setFieldValue(form, "max_pull_limit", queueSettings.max_pull_limit);
  setFieldValue(form, "operator_pull_enabled", queueSettings.operator_pull_enabled);
  setFieldValue(form, "manager_dispatch_enabled", queueSettings.manager_dispatch_enabled);
  setFieldValue(form, "manager_reassign_enabled", queueSettings.manager_reassign_enabled);
  setFieldValue(form, "manager_release_enabled", queueSettings.manager_release_enabled);
  setFieldValue(form, "auto_close_after_inactive_hours", queueSettings.auto_close_after_inactive_hours);

  if (featureEnabled(controlCenter, "queue_automation") && recommendation.automation_settings) {
    const automation = recommendation.automation_settings;
    setFieldValue(form, "auto_assign_enabled", automation.auto_assign_enabled);
    setFieldValue(form, "auto_assign_batch_size", automation.auto_assign_batch_size);
    setFieldValue(form, "auto_assign_max_open_per_user", automation.auto_assign_max_open_per_user);
    setFieldValue(form, "auto_escalate_overdue_enabled", automation.auto_escalate_overdue_enabled);
    setFieldValue(form, "auto_escalate_overdue_batch_size", automation.auto_escalate_overdue_batch_size);
  }
  if (featureEnabled(controlCenter, "queue_alerts") && recommendation.alert_settings) {
    const alerts = recommendation.alert_settings;
    setFieldValue(form, "alerts_enabled", alerts.enabled);
    setFieldValue(form, "cooldown_minutes", alerts.cooldown_minutes);
    setFieldValue(form, "overdue_total_threshold", alerts.overdue_total_threshold);
    setFieldValue(form, "overdue_unassigned_threshold", alerts.overdue_unassigned_threshold);
    setFieldValue(form, "first_response_overdue_threshold", alerts.first_response_overdue_threshold);
    setFieldValue(form, "automation_skipped_overdue_threshold", alerts.automation_skipped_overdue_threshold);
  }
  if (featureEnabled(controlCenter, "queue_digests") && recommendation.digest_settings) {
    const digests = recommendation.digest_settings;
    setFieldValue(form, "digest_enabled", digests.enabled);
    setFieldValue(form, "daily_enabled", digests.daily_enabled);
    setFieldValue(form, "weekly_enabled", digests.weekly_enabled);
    setFieldValue(form, "delivery_hour_utc", digests.delivery_hour_utc);
  }
  return true;
}
